import React, { useState, useEffect, useLayoutEffect, useMemo, useRef } from 'react';
import { ChordSegment } from '../utils/chordChartExport';
import {
    ChordRegion, Sounding, shownSegment, namedFor,
    countsInRegion, moveBoundary, regionAt, KeyRun, isEnabled, regionVoicing,
} from '../utils/midiAnalysis';
import { keyCenterColor, keyCenterLabel, keyCenterName, PickerAnchor } from './KeyCenterPicker';
import { KeyCenter, keyCentersEqual } from '../utils/keyCenterRegions';
import FollowToggle from './FollowToggle';

/** A chart key set on a chord dated to this region that differs from the file's key there. */
// A chart key change differing from the file's (chartFileLink.ts DivergentKey):
// `cleared`, the chart has none where the file has one; `changeRegionIndex`,
// where the file's key change landing here starts; `addedChord`, the chart
// chord not in the file that the key is set on, if it is
export type KeyMarker = {
    regionIndex: number; start: number; chordId: string; keyCenter: KeyCenter | null;
    cleared: boolean; changeRegionIndex: number | null; addedChord: string | null;
};

/**
 * The MIDI file player zoomed in: chord region edit mode. It takes over the
 * whole player panel and shows a stretch of the file in two strips:
 *
 * - The header: a band per chord region in its mode colour, with its symbol
 *   drawn as everywhere else in the app. Clicking a band selects the region,
 *   shift-clicking selects the run from the last one clicked, and ⌘ / Ctrl
 *   adds or takes out one (the player toggles on all their notes together
 *   while paused, so a merge can be heard before it is made); the playhead
 *   stays put. Two or more selected side by side can be merged.
 *   Dragging it moves the view instead, so a mouse with no wheel can do what
 *   a trackpad pinch does, as Ableton Live's ruler does: sideways pans, down
 *   zooms in and up zooms out (zoomLevelStep), in any mix. The pointer is locked
 *   and hidden for the whole drag, so it never runs out of screen, and a red
 *   line marks the point grabbed: it moves with the regions, stands in for
 *   the hidden cursor, and the zoom centres on it, even once it has gone out
 *   of view. At either end of the file it stops with the regions; turning
 *   back moves both again at once. On release the browser puts the cursor
 *   back where it was hidden, since a page can't place it.
 *   Pins are grabbed here only, and only the selected regions' own: their
 *   starts and ends. Dragging one moves the boundary two regions
 *   share, scrubbing the file as it goes so the keys follow the pin, and both
 *   regions take the notes the naming rule gives their new spans
 *   (utils/midiAnalysis.ts moveBoundary). A pin pushes the pins beyond it
 *   rather than squeeze a region narrower than MIN_REGION_PX on screen.
 * - The roll below, under a crosshair: a compressed piano roll (pitch
 *   without note names, the pedal's ring drawn fainter), with the notes that
 *   count for the selected or dragged region lit. Pressing here scrubs, as
 *   the progress bar does, and where the scrub is let go becomes the cue.
 *
 * Play always starts from the cue, drawn as a pulsing teal line, as Live's
 * start marker does: pausing leaves the playhead where it stopped, so the
 * last thing heard stays marked while the next play still starts from the
 * cue. Only a click in the roll moves the cue.
 *
 * Scroll pans; pinch (or ctrl + scroll) zooms around the pointer.
 */

interface MidiZoomViewProps {
    duration: number;
    currentTime: number;
    isPlaying: boolean;
    isScrubbing: boolean;
    notes: Array<{ note: number; time: number; duration: number; effectiveEndTime: number }>;
    regions: ChordRegion[];
    sounding: Sounding[];
    notationLegible: boolean;
    // A region's symbol, spelled in its key; `notesKey` is the voicingKey its
    // name was given for
    chordSymbol: (region: ChordRegion, segment: ChordSegment, notesKey: string) => JSX.Element;
    // Its root alone, spelled the same way
    chordRoot: (region: ChordRegion, segment: ChordSegment, notesKey: string) => string;
    noteName: (region: ChordRegion, pitchClass: number) => string;
    // The file's keys as stretches, with the analysis's confidence for each
    // auto one; a click in the lane sets the key from the region under it
    keyLane: (KeyRun & { confidence?: number })[];
    onKeyLaneClick: (regionIndex: number, anchor: PickerAnchor) => void;
    // The file's global key, set from the controls
    globalKey: KeyCenter | null;
    onGlobalKeyClick: (anchor: PickerAnchor) => void;
    // The analysis's fast key areas: null when it found none, else whether
    // they are in use
    fastKeyAreas: boolean | null;
    onToggleFastKeyAreas: () => void;
    // Chart keys that differ from the file's: a marker on the lane each, whose
    // menu settles it either way
    keyMarkers: KeyMarker[];
    onUseChartKey: (marker: KeyMarker) => void;
    onReturnChartKey: (marker: KeyMarker) => void;
    // The chart's global key when it differs from the file's: a marker beside
    // the file's global key, whose menu settles it either way
    globalKeyMarker: { keyCenter: KeyCenter | null } | null;
    onUseChartGlobalKey: () => void;
    onReturnChartGlobalKey: () => void;
    // Follow: the toggle, and whether play is locked while the chart plays
    followOn: boolean;
    onToggleFollow: () => void;
    playLocked: boolean;
    // The selected regions' indices. A click selects with the modifiers held:
    // shift for a run, toggle (⌘ / Ctrl) for one more or one fewer.
    selectedRegions: number[];
    onSelectRegion: (index: number, modifiers: { shift: boolean; toggle: boolean }) => void;
    // A drag's regions as it goes; onRegionEdit records the regions it began
    // from, once, when it ends
    onRegionsChange: (regions: ChordRegion[]) => void;
    onRegionEdit: (before: ChordRegion[]) => void;
    // A boundary drag began (true) or ended (false)
    onBoundaryDrag: (dragging: boolean) => void;
    // Follow off: the start of the region the selected chart chord came
    // from, outlined dashed (linked, the chart selects the region itself)
    chartSelectionStart: number | null;
    // Merge: two or more selected regions side by side become one
    canMerge: boolean;
    onMergeRegions: () => void;
    // Split the region under the playhead
    canSplitRegion: boolean;
    onSplitRegion: () => void;
    // Region edit mode: the region whose notes the piano is correcting, the
    // pencil that starts or ends it for the selected region, a double-click
    // on a band that starts it there, and the marker's choice of taking a
    // chart chord's notes
    editingRegion: number | null;
    canEditRegion: boolean;
    onToggleRegionEdit: () => void;
    onEditRegion: (index: number) => void;
    onUpdateRegionFromChart: (index: number, entryId: string) => void;
    // Show or hide the selected regions as chords (Shift+V); selectionEnabled
    // is whether the first selected one is shown
    canToggleEnabled: boolean;
    selectionEnabled: boolean;
    onToggleEnabled: () => void;
    // Add the selected regions to the chord chart, a chord each
    canPromote: boolean;
    promoteTitle: string;
    onPromote: () => void;
    // Regions whose chart chords the chart has changed since, with those
    // chords: each shows a marker whose menu updates the chart chords from the
    // region, or the region from one of them
    divergedRegions: { index: number; chords: { id: string; name: string }[] }[];
    onUpdateChartFromRegion: (index: number) => void;
    // Regions not in the chart that could be: each shows a ⊕ that adds it
    unchartedRegions: number[];
    onPromoteRegion: (index: number) => void;
    canUndo: boolean;
    canRedo: boolean;
    onUndo: () => void;
    onRedo: () => void;
    // Starts the player's own scrub at `time`; `timeAt` maps the pointer to a
    // file time for the rest of the drag
    onScrubStart: (time: number, timeAt: (clientX: number) => number) => void;
    // Pauses, or plays from `from`
    onTogglePlay: (from?: number) => void;
    // Where play starts from, held by the player so Shift+Space can use it
    cue: number;
    onCueChange: (cue: number) => void;
    onExit: () => void;
    // The playhead time and the speed menu, which belong to the player
    timeAndSpeed: React.ReactNode;
}

const MIN_SPAN = 0.5;
const MAX_SPAN = 120;
const DEFAULT_SPAN = 8;

// The narrowest a pushed region may become, on screen
const MIN_REGION_PX = 8;

// A band narrower than this has no room for its marker beside the symbol
const MARKER_MIN_BAND_PX = 40;

// The key lane along the top of the view, just above the header, as the
// chart's key centers sit above its chords: a line in the key's mode colour.
// A local key's name and arrow sit on black over the line, about three
// quarters of the box over the region before and the rest over the key's own
// region, past the boundary where the key starts. The global key is named in
// the controls, not here.
// No more than the line and a hair of gap: the header and roll keep the
// height they had before keys were drawn. A label hangs from the line over the
// header's top edge.
const KEY_LANE = '0.18dvw';
// The line, along the view's top edge
const KEY_LINE = '0.12dvw';
const keyLabelStyle = (left: string, onBoundary: boolean): React.CSSProperties => ({
    position: 'absolute', left, top: 0, transform: onBoundary ? 'translateX(calc(-75% + 0.15dvw))' : 'none',
    padding: '0.03dvw 0.2dvw', borderRadius: '0.25dvw', backgroundColor: '#000',
    fontFamily: 'Bravura Text', fontSize: '0.7dvw', lineHeight: 1, whiteSpace: 'nowrap',
    cursor: 'pointer', zIndex: 4,
});
// The header strip of chord regions, under the key lane
const HEADER = '1.6dvw';
// Where the roll starts, under both
const ROLL_TOP = `calc(${KEY_LANE} + ${HEADER})`;
const keyLaneColor = (mode: number, alpha: number) =>
    (mode === 1 ? `rgba(70, 70, 190, ${alpha})` : `rgba(150, 150, 0, ${alpha})`);
// How far a span lies along [from, to] on a log scale, 0 to 1
const logAlong = (span: number, from: number, to: number) =>
    (to > from ? Math.max(0, Math.min(1, Math.log(span / from) / Math.log(to / from))) : 0);

// The regions' chord symbol size (dvw) by zoom: largest fully zoomed in, a
// little smaller at the default span, and 30% under that fully zoomed out,
// on a log scale of the span in between
const SYMBOL_ZOOMED_IN = 1.37;
const SYMBOL_DEFAULT = 1.21;
const SYMBOL_ZOOMED_OUT = 0.85;
const symbolSize = (span: number, maxSpan: number) => {
    if (span <= DEFAULT_SPAN) return SYMBOL_ZOOMED_IN + (SYMBOL_DEFAULT - SYMBOL_ZOOMED_IN) * logAlong(span, MIN_SPAN, DEFAULT_SPAN);
    return SYMBOL_DEFAULT + (SYMBOL_ZOOMED_OUT - SYMBOL_DEFAULT) * logAlong(span, DEFAULT_SPAN, maxSpan);
};

// A pin line's width (dvw): full from the default span in, thinning towards
// fully zoomed out, where dozens of pins would otherwise fill the bar. Not in
// proportion with the symbols, which would make it vanish.
// Never under half a CSS pixel, one device pixel on a Retina screen.
const PIN_WIDTH = 0.1;
const PIN_WIDTH_ZOOMED_OUT = 0.025;
const pinWidth = (span: number, maxSpan: number) =>
    `max(0.5px, ${PIN_WIDTH + (PIN_WIDTH_ZOOMED_OUT - PIN_WIDTH) * logAlong(span, DEFAULT_SPAN, maxSpan)}dvw)`;

// A region band's corner radius (dvw), smaller zoomed out, where narrow bands
// would otherwise round into pills
const BAND_RADIUS = 0.3;
const BAND_RADIUS_ZOOMED_OUT = 0.16;
const bandRadius = (span: number, maxSpan: number) =>
    BAND_RADIUS + (BAND_RADIUS_ZOOMED_OUT - BAND_RADIUS) * logAlong(span, DEFAULT_SPAN, maxSpan);
// Where pin lines start, as a share of the radius. A line sits just inside its
// band, where the corner's curve has dropped only about a third of the radius.
const PIN_TOP_SHARE = 1 / 3;

// How far a press in the header moves before it is a drag, not a click
const DRAG_THRESHOLD_PX = 4;

// A header drag's zoom: down in, up out, at a speed set by how far zoomed the
// view is now. The zoom level runs from 0 (MIN_SPAN) to 1 (the widest span)
// on a log scale, and each pixel moves it by a rate that grows evenly from
// fully zoomed in to ZOOM_SPEED_RATIO times that fully zoomed out, so the
// whole range is ZOOM_RANGE_PX of travel. (With the 120 s widest span: x2
// takes ~110 px fully in, ~45 px at the default 8 s, ~27 px fully out.)
const ZOOM_RANGE_PX = 400;
const ZOOM_SPEED_RATIO = 4;
const ZOOM_RATE_IN = Math.log(ZOOM_SPEED_RATIO) / ((ZOOM_SPEED_RATIO - 1) * ZOOM_RANGE_PX);
const zoomLevelStep = (level: number, dy: number) =>
    Math.max(0, Math.min(1, level - dy * ZOOM_RATE_IN * (1 + (ZOOM_SPEED_RATIO - 1) * level)));

// A sideways drag is never perfectly level, and its stray up and down would
// zoom, plainly so at an end of the file, where nothing pans to hide it. So a
// drag zooms only while it heads more than about 22 degrees off horizontal:
// its smoothed vertical movement must be over VERTICAL_SHARE of its
// horizontal. Smoothed, because single mouse events jitter in any direction.
const VERTICAL_SHARE = 0.4;
const DIRECTION_SMOOTHING = 0.7;

// A pinch (or ctrl + scroll) zooms by the drag's rule: each unit of the
// wheel's deltaY counts as this many pixels of drag
const PINCH_PX_PER_DELTA = 0.65;

// The playhead, and the line a header drag holds still
const PLAYHEAD_COLOR = 'rgb(255, 120, 80)';

// A narrow band's short label, laid over its symbol's place on the same
// baseline: the band's own line height and top padding
const shortLabelStyle: React.CSSProperties = {
    position: 'absolute', left: '0.35dvw', top: 0, paddingTop: '0.1em', lineHeight: HEADER, visibility: 'hidden',
};
// Small, since at the symbols' size it looks heavy
const ellipsis = <span style={{ fontSize: '0.5em' }}>{'\u2026'}</span>;

// The Chord Symbol panel's outline for the chord selected in the chart
const SELECTED_OUTLINE = 'rgba(100, 255, 255, 0.75)';

const modeColor = (segment: ChordSegment | null | undefined, alpha: number) => {
    if (!segment) return `rgba(120, 120, 120, ${alpha})`;
    return segment.mode === 1 ? `rgba(70, 70, 190, ${alpha})` : `rgba(150, 150, 0, ${alpha})`;
};

const controlButtonStyle = (enabled: boolean): React.CSSProperties => ({
    height: '1.4dvw',
    width: '1.6dvw',
    // .midi-player button:active sets width auto; the minimums hold the size
    minWidth: '1.6dvw',
    minHeight: '1.4dvw',
    padding: 0,
    fontSize: '0.95dvw',
    fontWeight: 700,
    // Glyphs and icons centred in the box, whatever their font's line metrics
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    lineHeight: 1,
    borderRadius: '0.4dvw',
    cursor: enabled ? 'pointer' : 'auto',
    opacity: enabled ? 1 : 0.45,
    boxSizing: 'border-box',
});

// Split and merge draw a pin with arrows: pointing apart, and pointing in.
// U+FE0E keeps the triangles as text, not emoji, so they take the button's
// colour. Small and wider than the others, as three glyphs.
const SPLIT_GLYPH = '◀︎|▶︎';
const MERGE_GLYPH = '|▶◀︎|';
const wideButtonStyle = (enabled: boolean): React.CSSProperties => ({
    ...controlButtonStyle(enabled),
    width: '2.2dvw',
    minWidth: '2.2dvw',
    fontSize: '0.6dvw',
    letterSpacing: '-0.05dvw',
});
// A glyph's pins ('|') drawn as bars centred on the button, as the
// triangles are, rather than as the font's '|', which sits lower
const pinGlyph = (text: string) => (
    <span style={{ display: 'inline-flex', alignItems: 'center' }}>
        {text.split('|').map((part, i) => (
            <React.Fragment key={i}>
                {i > 0 && <span style={{ display: 'inline-block', width: '0.1em', height: '1.1em', margin: '0 0.1em', backgroundColor: 'currentColor' }} />}
                {part}
            </React.Fragment>
        ))}
    </span>
);
// On: white and glowing, as the app's Sound On button
const GLOW = '0 0 0.7dvw #fff, 0 0 0.7dvw #fff, 0 0 1.4dvw #bbb, 0 0 1.8dvw #bbb, 0 0 2.8dvw #bbb';
// A control naming a setting rather than drawing a glyph: as wide as its text
const textButtonStyle = (enabled: boolean): React.CSSProperties => ({
    ...controlButtonStyle(enabled),
    width: 'auto',
    padding: '0 0.4dvw',
    fontSize: '0.7dvw',
    whiteSpace: 'nowrap',
});

type Drag = { k: number; original: ChordRegion[]; minWidth: number };

// A press in the header: a click until it moves, then a drag. Once the
// pointer is locked only movementX / movementY say how far the mouse went.
type HeaderGesture = {
    x0: number;
    y0: number;
    dragging: boolean;
    // The zoom level, 0 fully in to 1 fully out (zoomLevelStep)
    level: number;
    // The drag's smoothed movement, which says whether it is heading up or
    // down enough to zoom (VERTICAL_SHARE)
    vx: number;
    vy: number;
    rectLeft: number;
    width: number;
    startViewStart: number;
    startSpan: number;
    // The file time grabbed, where the line is drawn
    grabTime: number;
    // Where the line is, in px from the view's left edge. Re-taken from the
    // view whenever it stops at an end of the file, so the line stays on the
    // regions and turning back moves them at once.
    lineX: number;
    regionIndex: number | null;
    // The modifiers held at the press, which the click selects with
    shift: boolean;
    toggle: boolean;
};

const MidiZoomView: React.FC<MidiZoomViewProps> = ({
    duration, currentTime, isPlaying, isScrubbing, notes, regions, sounding, notationLegible,
    chordSymbol, chordRoot, noteName, keyLane, onKeyLaneClick, globalKey, onGlobalKeyClick, fastKeyAreas, onToggleFastKeyAreas, keyMarkers, onUseChartKey, onReturnChartKey, globalKeyMarker, onUseChartGlobalKey, onReturnChartGlobalKey, followOn, onToggleFollow, playLocked, selectedRegions, onSelectRegion, onRegionsChange, onRegionEdit, onBoundaryDrag, chartSelectionStart,
    canMerge, onMergeRegions, canSplitRegion, onSplitRegion, editingRegion, canEditRegion, onToggleRegionEdit, onEditRegion, onUpdateRegionFromChart, canToggleEnabled, selectionEnabled, onToggleEnabled, canPromote, promoteTitle, onPromote, divergedRegions, onUpdateChartFromRegion, unchartedRegions, onPromoteRegion, canUndo, canRedo, onUndo, onRedo, onScrubStart, onTogglePlay, cue, onCueChange, onExit, timeAndSpeed,
}) => {
    const viewRef = useRef<HTMLDivElement>(null);
    const headerRef = useRef<HTMLDivElement>(null);
    const [span, setSpan] = useState(() => Math.min(DEFAULT_SPAN, duration || DEFAULT_SPAN));
    // The playhead a quarter in, unless that would run the view past the end
    // of the file: at the end, the view ends there, the playhead at its right
    const [viewStart, setViewStart] = useState(() => {
        const s = Math.min(DEFAULT_SPAN, duration || DEFAULT_SPAN);
        return Math.max(0, Math.min(duration - s, currentTime - s / 4));
    });
    const [drag, setDrag] = useState<Drag | null>(null);
    // The header press in progress; a ref because every mousemove updates it
    const gestureRef = useRef<HeaderGesture | null>(null);
    const [isGesturing, setIsGesturing] = useState(false);
    // The file time a header drag grabbed, where its line is drawn
    const [dragLineTime, setDragLineTime] = useState<number | null>(null);
    const [rollScrub, setRollScrub] = useState(false);
    // The region whose marker's menu is open, and where the marker is on screen
    const [markerMenu, setMarkerMenu] = useState<{ index: number; left: number; top: number } | null>(null);
    // The region under the pointer in the key lane or the header, whose key
    // arrow shows, as the chart shows one over the chord hovered
    const [hoveredRegion, setHoveredRegion] = useState<number | null>(null);
    // The key marker whose menu is open (null: the global key's), and where it is on screen
    const [keyMarkerMenu, setKeyMarkerMenu] = useState<{ marker: KeyMarker | null; left: number; top: number } | null>(null);

    const clampStart = (start: number, s: number) => Math.max(0, Math.min(Math.max(0, duration - s), start));

    // The zoom level on its log scale, from MIN_SPAN (0) to the widest span (1)
    const zoomOctaves = Math.log(Math.max(MIN_SPAN, Math.min(MAX_SPAN, duration)) / MIN_SPAN);
    const levelOfSpan = (s: number) => (zoomOctaves > 0 ? Math.log(s / MIN_SPAN) / zoomOctaves : 0);
    const spanAtLevel = (level: number) => MIN_SPAN * Math.exp(level * zoomOctaves);
    const viewEnd = viewStart + span;
    const pct = (t: number) => `${((t - viewStart) / span) * 100}%`;

    // The file time under the pointer, by the view as it is now
    const timeUnder = (clientX: number) => {
        const rect = viewRef.current ? viewRef.current.getBoundingClientRect() : null;
        if (!rect || rect.width === 0) return viewStart;
        return viewStart + ((clientX - rect.left) / rect.width) * span;
    };

    // Keep the playhead in view while the file plays: page forward when it
    // nears the right edge, and jump to it when it is somewhere else entirely.
    useEffect(() => {
        if (!isPlaying || isScrubbing) return;
        if (currentTime < viewStart || currentTime > viewStart + span * 0.9) {
            setViewStart(clampStart(currentTime - span * 0.1, span));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentTime, isPlaying]);

    // A paused playhead that jumps out of view (Follow's seek, << >>) brings
    // the view with it, a quarter in
    useEffect(() => {
        if (isPlaying || isScrubbing) return;
        if (currentTime < viewStart || currentTime > viewStart + span) {
            setViewStart(clampStart(currentTime - span / 4, span));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentTime]);

    // The view as the wheel last left it. A trackpad's momentum sends wheel
    // events faster than React renders, so each must build on the one before,
    // not on the last render: two built on the same render lost the first's
    // step, and as the steps shrink the view jumped back (flicker).
    const wheelViewRef = useRef({ start: viewStart, span });
    wheelViewRef.current = { start: viewStart, span };

    // A native listener, because React's wheel listeners are passive and could
    // not stop a pinch from zooming the whole page.
    useEffect(() => {
        const el = viewRef.current;
        if (!el) return;
        const onWheel = (e: WheelEvent) => {
            e.preventDefault();
            const rect = el.getBoundingClientRect();
            const view = wheelViewRef.current;
            let next = view;
            if (e.ctrlKey) {
                // Spreading (deltaY below 0) zooms in, as a drag down does
                const at = view.start + ((e.clientX - rect.left) / rect.width) * view.span;
                const nextSpan = spanAtLevel(zoomLevelStep(levelOfSpan(view.span), -e.deltaY * PINCH_PX_PER_DELTA));
                const fraction = (at - view.start) / view.span;
                next = { start: clampStart(at - fraction * nextSpan, nextSpan), span: nextSpan };
            } else {
                const delta = Math.abs(e.deltaX) > Math.abs(e.deltaY) ? e.deltaX : e.deltaY;
                next = { start: clampStart(view.start + (delta / rect.width) * view.span, view.span), span: view.span };
            }
            wheelViewRef.current = next;
            setSpan(next.span);
            setViewStart(next.start);
        };
        el.addEventListener('wheel', onWheel, { passive: false });
        return () => el.removeEventListener('wheel', onWheel);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [duration]);

    // A pin rides the scrub: each time the playhead moves, the boundary is
    // re-placed from where the drag began. When the scrub ends, so does the
    // drag, and it is recorded as one edit if it changed anything.
    useEffect(() => {
        if (drag === null) return;
        if (!isScrubbing) {
            if (regions !== drag.original) onRegionEdit(drag.original);
            setDrag(null);
            onBoundaryDrag(false);
            return;
        }
        onRegionsChange(moveBoundary(drag.original, drag.k, currentTime, drag.minWidth, sounding));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentTime, isScrubbing]);

    // A click in the roll moves the cue to wherever its scrub is let go
    useEffect(() => {
        if (rollScrub && !isScrubbing) {
            onCueChange(currentTime);
            setRollScrub(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isScrubbing, rollScrub]);

    // The header press: a click selects the region under it; a drag pans and
    // zooms around a line held still (see the header comment).
    useEffect(() => {
        if (!isGesturing) return;
        const onMove = (e: MouseEvent) => {
            const g = gestureRef.current;
            if (!g) return;
            if (!g.dragging) {
                if (Math.max(Math.abs(e.clientX - g.x0), Math.abs(e.clientY - g.y0)) < DRAG_THRESHOLD_PX) return;
                // The point grabbed is where the cursor is hidden
                g.dragging = true;
                g.lineX = e.clientX - g.rectLeft;
                g.grabTime = g.startViewStart + (g.lineX / g.width) * g.startSpan;
                setDragLineTime(g.grabTime);
                // Refused (or unsupported), the drag still works; the hidden
                // cursor then reappears wherever the mouse went
                const el = viewRef.current;
                if (el && el.requestPointerLock) {
                    try {
                        const request: any = el.requestPointerLock();
                        if (request && request.catch) request.catch(() => undefined);
                    } catch (err) { /* the cursor just moves */ }
                }
                return;
            }
            g.vx = g.vx * DIRECTION_SMOOTHING + e.movementX * (1 - DIRECTION_SMOOTHING);
            g.vy = g.vy * DIRECTION_SMOOTHING + e.movementY * (1 - DIRECTION_SMOOTHING);
            if (Math.abs(g.vy) > Math.abs(g.vx) * VERTICAL_SHARE) g.level = zoomLevelStep(g.level, e.movementY);
            const nextSpan = spanAtLevel(g.level);
            // The grabbed time stays under the line, which follows the mouse
            g.lineX += e.movementX;
            const nextStart = clampStart(g.grabTime - (g.lineX / g.width) * nextSpan, nextSpan);
            g.lineX = ((g.grabTime - nextStart) / nextSpan) * g.width;
            setSpan(nextSpan);
            setViewStart(nextStart);
        };
        const finish = (select: boolean) => {
            const g = gestureRef.current;
            if (select && g && !g.dragging && g.regionIndex !== null) onSelectRegion(g.regionIndex, { shift: g.shift, toggle: g.toggle });
            if (document.pointerLockElement === viewRef.current) document.exitPointerLock();
            gestureRef.current = null;
            setIsGesturing(false);
            setDragLineTime(null);
        };
        const onUp = () => finish(true);
        const onBlur = () => finish(false);
        document.addEventListener('mousemove', onMove);
        document.addEventListener('mouseup', onUp);
        window.addEventListener('blur', onBlur);
        return () => {
            document.removeEventListener('mousemove', onMove);
            document.removeEventListener('mouseup', onUp);
            window.removeEventListener('blur', onBlur);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isGesturing]);

    const pressHeader = (e: React.MouseEvent) => {
        e.preventDefault();
        const rect = viewRef.current ? viewRef.current.getBoundingClientRect() : null;
        if (!rect || rect.width === 0) return;
        const k = regionAt(regions, timeUnder(e.clientX));
        gestureRef.current = {
            x0: e.clientX,
            y0: e.clientY,
            dragging: false,
            level: levelOfSpan(span),
            vx: 0,
            vy: 0,
            rectLeft: rect.left,
            width: rect.width,
            startViewStart: viewStart,
            startSpan: span,
            grabTime: 0,
            lineX: 0,
            regionIndex: k >= 0 ? regions[k].index : null,
            shift: e.shiftKey,
            toggle: e.metaKey || e.ctrlKey,
        };
        setIsGesturing(true);
    };

    // Grabbing pin k: the scrub runs between the furthest the pushes allow
    // (the first pin down to the start of the file)
    const grabPin = (k: number) => {
        const rect = viewRef.current ? viewRef.current.getBoundingClientRect() : null;
        const minWidth = rect && rect.width > 0 ? (MIN_REGION_PX / rect.width) * span : 0;
        const n = regions.length;
        const min = k * minWidth;
        const max = regions[n - 1].end - (n - k) * minWidth;
        setDrag({ k, original: regions, minWidth });
        onBoundaryDrag(true);
        onScrubStart(regions[k].start, (x: number) => Math.max(min, Math.min(max, timeUnder(x))));
    };

    const scrubRoll = (clientX: number) => {
        const timeAt = (x: number) => Math.max(0, Math.min(duration, timeUnder(x)));
        setRollScrub(true);
        onScrubStart(timeAt(clientX), timeAt);
    };

    // The roll's pitch axis spans the file's own range, so a narrow piece
    // still fills the height.
    const pitchRange = useMemo(() => {
        let lo = 127;
        let hi = 0;
        notes.forEach(n => { lo = Math.min(lo, n.note); hi = Math.max(hi, n.note); });
        // A note the user added may lie outside the file's own range
        regions.forEach(r => (r.addedNotes || []).forEach(note => { lo = Math.min(lo, note); hi = Math.max(hi, note); }));
        return lo > hi ? { lo: 0, hi: 127 } : { lo, hi };
    }, [notes, regions]);

    const visibleNotes = useMemo(
        () => notes.filter(n => n.time < viewEnd && n.effectiveEndTime > viewStart),
        [notes, viewStart, viewEnd]
    );
    const visibleRegions = regions.filter(r => r.start < viewEnd && r.end > viewStart);

    const isSelected = (index: number) => selectedRegions.includes(index);
    // The regions whose notes the roll lights: the one starting at a dragged
    // pin, else the selected ones
    const litRegions = drag !== null ? [regions[drag.k]] : regions.filter(r => isSelected(r.index) || r.index === editingRegion);

    const regionLabel = (region: ChordRegion) => {
        if (region.is_scale) {
            // Rooted on the lowest note, as the backend roots a scale (its
            // scale_name carries only the mode, after a figure space)
            const root = noteName(region, Math.min(...regionVoicing(region)) % 12);
            return `${root}${region.scale_name || '\u2007scale'}`;
        }
        // A region being re-named shows its old name until the new one
        // arrives; only a region never named shows the ellipsis
        const segment = shownSegment(region, notationLegible);
        if (segment === undefined) return '…';
        if (!segment) return '–';
        return chordSymbol(region, segment, namedFor(region, notationLegible));
    };

    // The root a band too narrow for its symbol shows before an ellipsis, or
    // '' for a region with no name to take it from
    const regionRoot = (region: ChordRegion) => {
        if (region.is_scale) return noteName(region, Math.min(...regionVoicing(region)) % 12);
        const segment = shownSegment(region, notationLegible);
        if (!segment) return '';
        return chordRoot(region, segment, namedFor(region, notationLegible));
    };

    // A symbol wider than its band gives way to its root and an ellipsis,
    // then to an ellipsis alone, then to nothing, rather than being cut off.
    // Measured after every render, since zoom, drags and new names all change
    // the fit; hidden spans keep their widths, so the symbol shows again once
    // it fits. All bands are read before any is changed, so layout is worked
    // out once.
    useLayoutEffect(() => {
        const header = headerRef.current;
        if (!header) return;
        const bands = Array.from(header.children) as HTMLElement[];
        const shown = bands.map(band => {
            const labels = Array.from(band.children) as HTMLElement[];
            if (labels.length < 3) return -1;
            const room = band.clientWidth - labels[0].offsetLeft + 1;
            // The root form only when there is a root to show
            return labels.findIndex((label, i) => (i !== 1 || label.textContent !== '\u2026') && label.offsetWidth <= room);
        });
        bands.forEach((band, i) => {
            Array.from(band.children).forEach((label, j) => {
                (label as HTMLElement).style.visibility = j === shown[i] ? 'visible' : 'hidden';
            });
        });
    });

    // The keys named in the lane: the global key showing through is named in the controls
    // Every key set in the file, "use global key" markers too, which show
    // the global key where they change the key, and the arrow alone where the
    // key before was the global key already, or there is none
    const localKeyRuns = keyLane.filter(run => run.source !== 'global');
    const silentMarkers = new Set(keyLane
        .filter((run, i) => run.usesGlobal && (i === 0 || keyCentersEqual(keyLane[i - 1].keyCenter, run.keyCenter)))
        .map(run => run.regionIndex));
    // A label sits across where its key starts, or starts at the view's left
    // edge when its key starts there or before
    const keyLabelAt = (start: number) => (start <= viewStart ? keyLabelStyle('0', false) : keyLabelStyle(pct(start), true));
    const openKeyPicker = (regionIndex: number) => (e: React.MouseEvent<HTMLElement>) => {
        e.preventDefault();
        e.stopPropagation();
        const rect = e.currentTarget.getBoundingClientRect();
        onKeyLaneClick(regionIndex, { bottom: rect.bottom, centerX: rect.left + rect.width / 2, top: rect.top });
    };

    const rows = pitchRange.hi - pitchRange.lo + 1;
    const maxSpan = Math.min(MAX_SPAN, duration);
    const radius = bandRadius(span, maxSpan);

    return (
        <div style={{ display: 'flex', flex: 1, alignSelf: 'stretch', gap: '0.4dvw', minWidth: 0 }}>
            <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: '0.25dvw' }}>
                <div style={{ display: 'flex', gap: '0.2dvw' }}>
                    <button onClick={onExit} title="Zoom out" style={controlButtonStyle(true)}>{'\u2921'}</button>
                    {/* White while playing, glowing and pressed in, as the
                        player's own play button is, scaled to this one's size */}
                    <button onClick={() => onTogglePlay(isPlaying ? undefined : cue)} disabled={playLocked} title={(() => {
                        if (playLocked) return 'The chord chart is playing';
                        return isPlaying ? 'Pause' : 'Play from the teal line';
                    })()} style={{
                        ...controlButtonStyle(!playLocked),
                        color: isPlaying ? '#fff' : '#000',
                        textShadow: isPlaying ? '0 0 0.55dvw #000, 0 0 0.55dvw #000, 0 0 0.3dvw #000, 0 0 1.4dvw #000, 0 0 2.2dvw #000' : '',
                        boxShadow: isPlaying ? 'inset 0.15dvw 0.15dvw 0.4dvw #1a1a1a, inset -0.15dvw -0.15dvw 0.4dvw #4a4a4a' : undefined,
                    }}>
                        {isPlaying ? '\u23ef' : '\u23f5'}
                    </button>
                    <button onClick={onSplitRegion} disabled={!canSplitRegion} title="Split the chord region at the playhead" style={wideButtonStyle(canSplitRegion)}>{pinGlyph(SPLIT_GLYPH)}</button>
                    <button onClick={onMergeRegions} disabled={!canMerge} title="Merge the selected chord regions (two or more side by side)" style={wideButtonStyle(canMerge)}>{pinGlyph(MERGE_GLYPH)}</button>
                    <button onClick={onPromote} disabled={!canPromote} title={promoteTitle} style={controlButtonStyle(canPromote)}>
                        <img src="/images/edit-file.png" alt="Add to chord chart" style={{ width: '0.95dvw', height: '0.95dvw', verticalAlign: 'middle' }} />
                    </button>
                    {/* Shown or hidden: ⦿ white and glowing while the selection
                        is shown as chords, ⊘ black while it is hidden as melody */}
                    <button
                        onClick={onToggleEnabled}
                        disabled={!canToggleEnabled}
                        title={selectionEnabled
                            ? 'Hide the selected regions: melody, not chords (Shift+V)'
                            : 'Show the selected regions as chords again (Shift+V)'}
                        style={{
                            ...controlButtonStyle(canToggleEnabled),
                            color: selectionEnabled ? '#fff' : '#000',
                            textShadow: selectionEnabled ? GLOW : '',
                        }}
                    >{selectionEnabled ? '\u29BF' : '\u2298'}</button>
                    {/* The chart's pencil: correct the selected region's notes on the piano */}
                    <button
                        onClick={onToggleRegionEdit}
                        disabled={editingRegion === null && !canEditRegion}
                        title={editingRegion !== null
                            ? 'Done correcting this region\'s notes (Enter)'
                            : 'Correct the selected region\'s notes: toggle keys on the piano to add or strike out notes'}
                        style={{
                            ...controlButtonStyle(editingRegion !== null || canEditRegion),
                            ...(editingRegion !== null ? { outline: '0.1dvw solid white', animation: 'pulse-outline-bw 0.5s linear infinite' } : {}),
                        }}
                    >
                        <img src="/images/Pencil--Streamline-Core-Remix-white.png" alt="Correct notes" style={{ width: '0.8dvw', height: '0.8dvw', verticalAlign: 'middle' }} />
                    </button>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.2dvw' }}>
                    {timeAndSpeed}
                    <button onClick={onUndo} disabled={!canUndo} title="Undo" style={controlButtonStyle(canUndo)}>{'\u21BA'}</button>
                    <button onClick={onRedo} disabled={!canRedo} title="Redo" style={controlButtonStyle(canRedo)}>{'\u21BB'}</button>
                    <FollowToggle on={followOn} onToggle={onToggleFollow} size="0.85dvw" buttonStyle={controlButtonStyle(true)} />
                    {fastKeyAreas !== null && (
                        <button
                            onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); onToggleFastKeyAreas(); }}
                            title={fastKeyAreas ? 'Use the analysis\'s usual key areas' : 'Use the analysis\'s fast-changing key areas'}
                            style={{ ...textButtonStyle(true), color: fastKeyAreas ? '#fff' : '#000', textShadow: fastKeyAreas ? GLOW : '' }}
                        >fast keys</button>
                    )}
                    {/* The chart's global key differs from the file's: its marker,
                        just before the file's global key */}
                    {globalKeyMarker !== null && (
                        <div
                            className="midi-marker"
                            onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); }}
                            onClick={(e) => {
                                const rect = e.currentTarget.getBoundingClientRect();
                                setKeyMarkerMenu({ marker: null, left: rect.left, top: rect.top });
                            }}
                            title={globalKeyMarker.keyCenter
                                ? `The chord chart's global key is ${keyCenterName(globalKeyMarker.keyCenter)}`
                                : 'The chord chart has no global key'}
                            style={{ marginLeft: 'auto', fontSize: '0.85dvw', lineHeight: 1 }}
                        >{'⚠︎'}</div>
                    )}
                    {/* The file's global key, drawn as the chart's own, right-justified
                        under the last button of the row above */}
                    <div
                        className="chart-key-center"
                        onMouseDown={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            const rect = e.currentTarget.getBoundingClientRect();
                            onGlobalKeyClick({ bottom: rect.bottom, centerX: rect.left + rect.width / 2, top: rect.top });
                        }}
                        title="The file's global key"
                        style={{ marginTop: 0, marginLeft: globalKeyMarker !== null ? 0 : 'auto', fontSize: '1dvw', color: globalKey === null ? 'rgba(255, 255, 255, 0.9)' : keyCenterColor(globalKey.mode), opacity: globalKey === null ? 0.5 : 1 }}
                    >{globalKey === null ? 'key' : keyCenterLabel(globalKey)}{'\u25BE'}</div>
                </div>
            </div>
            {/* The view and, over it, the local keys' names, which open the key
                picker for their regions. The view clips everything to its box;
                the names are drawn outside it so a minor key's enlarged letter
                can rise above the view's top edge, clipped at its sides and
                foot as the view is. */}
            <div style={{ position: 'relative', flex: 1, minWidth: 0, display: 'flex' }}>
                <div
                    ref={viewRef}
                    onMouseMove={(e) => {
                        // The arrow reaches over the region before: pointing at it keeps it
                        if ((e.target as HTMLElement).dataset.keyArrow) return;
                        const header = headerRef.current;
                        const inTop = header !== null && e.clientY < header.getBoundingClientRect().bottom;
                        const k = inTop ? regionAt(regions, timeUnder(e.clientX)) : -1;
                        setHoveredRegion(k < 0 ? null : regions[k].index);
                    }}
                    onMouseLeave={() => setHoveredRegion(null)}
                    style={{
                        position: 'relative',
                        flex: 1,
                        overflow: 'hidden',
                        backgroundColor: 'rgba(0, 0, 0, 0.45)',
                        borderRadius: '0.2dvw',
                    }}
                >
                    {/* Header: click a band to select its region */}
                    <div
                        ref={headerRef}
                        onMouseDown={pressHeader}
                        onDoubleClick={(e) => {
                            const k = regionAt(regions, timeUnder(e.clientX));
                            if (k >= 0) onEditRegion(regions[k].index);
                        }}
                        style={{ position: 'absolute', left: 0, right: 0, top: KEY_LANE, height: HEADER, cursor: 'pointer' }}
                    >
                        {visibleRegions.map(region => {
                            const segment = shownSegment(region, notationLegible);
                            const selected = isSelected(region.index);
                            return (
                                <div key={`band-${region.index}`} style={{
                                    position: 'absolute',
                                    left: pct(region.start),
                                    width: `${((region.end - region.start) / span) * 100}%`,
                                    top: 0,
                                    height: '100%',
                                    backgroundColor: modeColor(segment, selected ? 0.8 : 0.35),
                                    // A hidden region (melody) is drawn faint, symbol and all
                                    opacity: isEnabled(region) ? 1 : 0.4,
                                    borderRadius: `${radius}dvw`,
                                    color: '#fff',
                                    fontFamily: 'Bravura Text',
                                    fontSize: `${symbolSize(span, maxSpan)}dvw`,
                                    lineHeight: HEADER,
                                    // Bravura sits high in its line; this centres
                                    // it, in em so it scales with the zoom's size
                                    paddingTop: '0.1em',
                                    paddingLeft: '0.35dvw',
                                    boxSizing: 'border-box',
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    pointerEvents: 'none',
                                }}>
                                    <span>{regionLabel(region)}</span>
                                    {/* Shown instead when the symbol does not fit: its
                                        root and an ellipsis, then the ellipsis alone */}
                                    <span style={shortLabelStyle}>{regionRoot(region)}{ellipsis}</span>
                                    <span style={shortLabelStyle}>{ellipsis}</span>
                                </div>
                            );
                        })}
                    </div>

                    {/* Roll: press to scrub */}
                    <div
                        onMouseDown={(e) => { e.preventDefault(); scrubRoll(e.clientX); }}
                        style={{ position: 'absolute', left: 0, right: 0, top: ROLL_TOP, bottom: 0, cursor: 'crosshair' }}
                    >
                        {visibleNotes.map((n, i) => {
                            const bottom = `${((n.note - pitchRange.lo) / rows) * 100}%`;
                            const height = `max(1px, ${100 / rows}%)`;
                            const held = Math.min(n.duration, n.effectiveEndTime - n.time);
                            const litIn = litRegions.find(r => countsInRegion(n.time, n.effectiveEndTime, r.start, r.end, r.heard));
                            const lit = litIn !== undefined;
                            // A file note the user struck out of the region: red
                            const struck = lit && (litIn!.removedNotes || []).includes(n.note);
                            return (
                                <React.Fragment key={i}>
                                    <div style={{
                                        position: 'absolute', left: pct(n.time), bottom, height,
                                        width: `${(held / span) * 100}%`,
                                        backgroundColor: (() => {
                                            if (struck) return 'rgb(255, 70, 70)';
                                            return lit ? 'rgb(255, 255, 255)' : 'rgba(120, 220, 220, 0.85)';
                                        })(),
                                        pointerEvents: 'none',
                                    }} />
                                    {n.effectiveEndTime > n.time + held && (
                                        <div style={{
                                            position: 'absolute', left: pct(n.time + held), bottom, height,
                                            width: `${((n.effectiveEndTime - n.time - held) / span) * 100}%`,
                                            backgroundColor: (() => {
                                                if (struck) return 'rgba(255, 70, 70, 0.5)';
                                                return lit ? 'rgba(255, 255, 255, 0.5)' : 'rgba(120, 220, 220, 0.25)';
                                            })(),
                                            pointerEvents: 'none',
                                        }} />
                                    )}
                                </React.Fragment>
                            );
                        })}
                        {/* Notes the user added: bright green, across the whole region,
                            while it is selected or being corrected. Not in the file:
                            playback and scrubbing never hear them. */}
                        {litRegions.filter(r => r.start < viewEnd && r.end > viewStart).map(r => (r.addedNotes || []).map(note => (
                            <div key={`added-${r.index}-${note}`} style={{
                                position: 'absolute', left: pct(r.start),
                                bottom: `${((note - pitchRange.lo) / rows) * 100}%`,
                                height: `max(1px, ${100 / rows}%)`,
                                width: `${((r.end - r.start) / span) * 100}%`,
                                backgroundColor: 'rgb(60, 255, 90)',
                                pointerEvents: 'none',
                            }} />
                        )))}
                    </div>

                    {/* Key lane: press to set the key from the region under the pointer */}
                    <div
                        onMouseDown={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            const k = regionAt(regions, timeUnder(e.clientX));
                            if (k < 0) return;
                            const rect = e.currentTarget.getBoundingClientRect();
                            onKeyLaneClick(regions[k].index, { bottom: rect.bottom, centerX: e.clientX, top: rect.top });
                        }}
                        title="Set the key from here"
                        style={{ position: 'absolute', left: 0, right: 0, top: 0, height: KEY_LANE, cursor: 'pointer', zIndex: 1 }}
                    >
                        {keyLane.filter(run => run.start < viewEnd && run.end > viewStart && run.keyCenter !== null).map(run => {
                            // The analysis's own keys by its confidence; the user's
                            // plainly, the global key showing through faintly
                            const alpha = run.source === 'auto' && run.confidence !== undefined ? 0.35 + 0.65 * run.confidence : 1;
                            return (
                                <div key={`key-${run.regionIndex}`} style={{
                                    position: 'absolute',
                                    left: pct(run.start),
                                    width: `${((run.end - run.start) / span) * 100}%`,
                                    top: 0,
                                    height: KEY_LINE,
                                    backgroundColor: keyLaneColor(run.keyCenter!.mode, run.source === 'global' || run.usesGlobal ? 0.45 : alpha),
                                    pointerEvents: 'none',
                                }} />
                            );
                        })}
                    </div>

                    {/* The hovered region's bare arrow where no key starts: it opens
                        the key picker for its region. The next region's arrow is
                        there unseen, over the hovered region's end, and shows when
                        the pointer finds it, so an arrow can be reached from either
                        side of its boundary. The local keys' names are drawn over
                        the view, below. */}
                    {visibleRegions
                        .filter(r => {
                            const k = regions.indexOf(r);
                            const hovered = r.index === hoveredRegion || (k > 0 && regions[k - 1].index === hoveredRegion);
                            return hovered && !localKeyRuns.some(run => run.regionIndex === r.index);
                        })
                        .map(region => (
                            <span
                                key={`key-arrow-${region.index}`}
                                data-key-arrow="true"
                                onMouseEnter={() => setHoveredRegion(region.index)}
                                onMouseDown={openKeyPicker(region.index)}
                                title="Key center from this chord on"
                                style={{ ...keyLabelAt(region.start), color: 'rgba(255, 255, 255, 0.6)', opacity: region.index === hoveredRegion ? 1 : 0 }}
                            >{'\u25BE'}</span>
                        ))}

                    {/* Pin lines run the full height; only their header part is grabbed */}
                    {visibleRegions.map(region => {
                        const k = regions.indexOf(region);
                        const segment = shownSegment(region, notationLegible);
                        // Only the selected regions' own pins (their starts and
                        // ends) can be grabbed, so a click meant to select a
                        // region never moves a boundary, and zoomed far out the
                        // bands stay clickable between pins packed close together
                        const draggable = isSelected(region.index) || (k > 0 && isSelected(regions[k - 1].index));
                        return (
                            <React.Fragment key={`pin-${region.index}`}>
                                <div style={{
                                    position: 'absolute', left: pct(region.start), width: pinWidth(span, maxSpan), top: `calc(${KEY_LANE} + ${radius * PIN_TOP_SHARE}dvw)`, bottom: 0,
                                    backgroundColor: drag !== null && drag.k === k ? '#fff' : modeColor(segment, 0.5),
                                    pointerEvents: 'none', zIndex: 2,
                                }} />
                                {draggable && (
                                    <div
                                        onMouseDown={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            grabPin(k);
                                        }}
                                        title="Drag to move this chord boundary"
                                        style={{
                                            position: 'absolute',
                                            left: `calc(${pct(region.start)} - 0.35dvw)`,
                                            width: '0.8dvw',
                                            top: KEY_LANE,
                                            height: HEADER,
                                            cursor: 'ew-resize',
                                            // Under the key labels and arrow, which sit on the
                                            // boundary too: the handle is grabbed below them
                                            zIndex: 3,
                                        }}
                                    />
                                )}
                            </React.Fragment>
                        );
                    })}

                    {/* Follow off: the region the selected chart chord came from, dashed */}
                    {chartSelectionStart !== null && visibleRegions.filter(r => Math.abs(r.start - chartSelectionStart) < 0.001).map(region => (
                        <div key={`chart-selection-${region.index}`} style={{
                            position: 'absolute',
                            left: pct(region.start),
                            width: `${((region.end - region.start) / span) * 100}%`,
                            top: KEY_LANE,
                            height: HEADER,
                            border: '0.08dvw dashed rgba(120, 220, 220, 0.9)',
                            borderRadius: `${radius}dvw`,
                            boxSizing: 'border-box',
                            pointerEvents: 'none',
                            zIndex: 3,
                        }} />
                    ))}

                    {/* The selected regions' outlines, over the pin lines they sit between */}
                    {visibleRegions.filter(r => isSelected(r.index)).map(region => (
                        <div key={`selected-outline-${region.index}`} style={{
                            // Pulsing while its notes are being corrected, as a chart chord in edit mode
                            ...(region.index === editingRegion ? { animation: 'pulse-outline-bw 0.5s linear infinite', outline: '0.1dvw solid white' } : {}),
                            position: 'absolute',
                            left: pct(region.start),
                            width: `${((region.end - region.start) / span) * 100}%`,
                            top: KEY_LANE,
                            height: HEADER,
                            border: `0.08dvw solid ${SELECTED_OUTLINE}`,
                            borderRadius: `${radius}dvw`,
                            boxSizing: 'border-box',
                            pointerEvents: 'none',
                            zIndex: 3,
                        }} />
                    ))}

                    {/* A marker at the right end of a band whose chart chords the chart
                        has changed since, when the band has room for it. It sits
                        above the header, so pressing it opens its menu instead of
                        starting a header gesture. */}
                    {visibleRegions.filter(r => divergedRegions.some(d => d.index === r.index)).map(region => {
                        const viewWidth = viewRef.current ? viewRef.current.clientWidth : 0;
                        const bandPx = ((region.end - region.start) / span) * viewWidth;
                        if (bandPx < MARKER_MIN_BAND_PX) return null;
                        return (
                            <div
                                key={`marker-${region.index}`}
                                className="midi-marker"
                                onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); }}
                                onClick={(e) => {
                                    const rect = e.currentTarget.getBoundingClientRect();
                                    setMarkerMenu(markerMenu && markerMenu.index === region.index ? null : { index: region.index, left: rect.left, top: rect.top });
                                }}
                                title="The chord chart has changed this chord since it was added"
                                style={{
                                    position: 'absolute',
                                    left: `calc(${pct(region.end)} - 1.2dvw)`,
                                    top: KEY_LANE,
                                    width: '1dvw',
                                    height: HEADER,
                                    lineHeight: HEADER,
                                    textAlign: 'center',
                                    fontSize: '0.85dvw',
                                    zIndex: 7,
                                }}
                            >{'\u26A0\uFE0E'}</div>
                        );
                    })}

                    {/* A ⊕ in the same place on a band not yet in the chart, which
                        adds it. A band is never both: the ⚠ needs a chart chord. */}
                    {visibleRegions.filter(r => unchartedRegions.includes(r.index)).map(region => {
                        const viewWidth = viewRef.current ? viewRef.current.clientWidth : 0;
                        const bandPx = ((region.end - region.start) / span) * viewWidth;
                        if (bandPx < MARKER_MIN_BAND_PX) return null;
                        return (
                            <div
                                key={`promote-${region.index}`}
                                className="midi-marker midi-marker-add"
                                onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); }}
                                onClick={() => onPromoteRegion(region.index)}
                                title="Add this chord region to the chord chart"
                                style={{
                                    position: 'absolute',
                                    left: `calc(${pct(region.end)} - 1.2dvw)`,
                                    top: KEY_LANE,
                                    width: '1dvw',
                                    height: HEADER,
                                    lineHeight: HEADER,
                                    textAlign: 'center',
                                    fontSize: '0.85dvw',
                                    zIndex: 7,
                                }}
                            >{'\u2295'}</div>
                        );
                    })}

                    {/* A header drag hides the cursor over the whole page (the lock
                        hides it too, but may be refused) and marks the point grabbed */}
                    {dragLineTime !== null && (
                        <>
                            <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 1000, cursor: 'none' }} />
                            <div style={{
                                position: 'absolute', left: pct(dragLineTime), top: 0, bottom: 0,
                                width: '0.08dvw', backgroundColor: PLAYHEAD_COLOR, pointerEvents: 'none', zIndex: 6,
                            }} />
                        </>
                    )}

                    {/* The cue, where play starts, pulsing teal to white */}
                    {cue >= viewStart && cue <= viewEnd && (
                        <div className="midi-cue-line" style={{
                            position: 'absolute', left: pct(cue), top: 0, bottom: 0,
                            width: '0.08dvw', pointerEvents: 'none', zIndex: 5,
                        }} />
                    )}

                    {/* Playhead: where playback is, or where it stopped. Standing on
                        the cue it is left to the cue's line. */}
                    {currentTime >= viewStart && currentTime <= viewEnd && (isPlaying || isScrubbing || Math.abs(currentTime - cue) > 1e-6) && (
                        <div style={{
                            position: 'absolute', left: pct(currentTime), top: 0, bottom: 0,
                            width: '0.08dvw', backgroundColor: PLAYHEAD_COLOR, pointerEvents: 'none', zIndex: 5,
                        }} />
                    )}
                </div>
                <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, clipPath: 'inset(-1dvw 0 0 0)', pointerEvents: 'none', zIndex: 4 }}>
                    {localKeyRuns.filter(run => run.start < viewEnd && run.end > viewStart).map(run => (
                        <span
                            key={`key-label-${run.regionIndex}`}
                            onMouseDown={openKeyPicker(run.regionIndex)}
                            title="Key center from this chord on"
                            style={{
                                ...keyLabelAt(run.start),
                                color: run.keyCenter !== null && !silentMarkers.has(run.regionIndex) ? keyCenterColor(run.keyCenter.mode) : 'rgba(255, 255, 255, 0.6)',
                                pointerEvents: 'auto',
                                fontStyle: run.source === 'manual' ? 'normal' : 'italic',
                                fontWeight: run.source === 'manual' ? 700 : 100,
                            }}
                        >{run.keyCenter !== null && !silentMarkers.has(run.regionIndex) && keyCenterLabel(run.keyCenter)}{'\u25BE'}</span>
                    ))}
                    {/* A chart key that differs from the file's: a marker hanging
                        from the line just past the key's boundary, on a box so it
                        reads over the header's band */}
                    {keyMarkers.filter(m => m.start >= viewStart && m.start < viewEnd).map(marker => (
                        <div
                            key={`key-marker-${marker.chordId}`}
                            className="midi-marker"
                            onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); }}
                            onClick={(e) => {
                                e.stopPropagation();
                                const rect = e.currentTarget.getBoundingClientRect();
                                setKeyMarkerMenu({ marker, left: rect.left, top: rect.top });
                            }}
                            title={(() => {
                                if (marker.cleared) return 'The chord chart has cleared this key change';
                                return `The chord chart has ${marker.keyCenter ? `the key ${keyCenterName(marker.keyCenter)}` : 'the global key'} here`;
                            })()}
                            style={{
                                position: 'absolute', left: `calc(${pct(marker.start)} + 0.6dvw)`, top: 0,
                                padding: '0.05dvw 0.2dvw', borderRadius: '0.25dvw', backgroundColor: 'rgba(0, 0, 0, 0.6)',
                                fontSize: '0.65dvw', lineHeight: 1, pointerEvents: 'auto', zIndex: 4,
                            }}
                        >{'\u26A0\uFE0E'}</div>
                    ))}
                </div>
            </div>

            {/* A key marker's menu, above it: the player is at the foot of the screen */}
            {keyMarkerMenu !== null && (
                <>
                    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 999 }} onClick={() => setKeyMarkerMenu(null)} />
                    <div style={{
                        position: 'fixed', left: keyMarkerMenu.left, bottom: window.innerHeight - keyMarkerMenu.top + 4, zIndex: 1000,
                        backgroundColor: '#1a1a1a', border: '0.05dvw solid rgba(255, 255, 255, 0.3)', borderRadius: '0.3dvw',
                        padding: '0.2dvw 0', fontSize: '0.75dvw', color: '#fff', whiteSpace: 'nowrap',
                    }}>
                        {(() => {
                            const marker = keyMarkerMenu.marker;
                            return marker === null ? [
                                { label: "Use the chart's global key in the analysis", act: onUseChartGlobalKey },
                                { label: "Return the chart to the file's global key", act: onReturnChartGlobalKey },
                            ] : [
                                {
                                    label: marker.cleared ? 'Clear this key change in the analysis too' : "Use the chart's key in the analysis",
                                    act: () => onUseChartKey(marker),
                                },
                                {
                                    label: marker.addedChord === null
                                        ? "Return the chart to the file's key"
                                        : `Return the chart to the file's key (removes the key on ${marker.addedChord})`,
                                    act: () => onReturnChartKey(marker),
                                },
                            ];
                        })().map(item => (
                            <div
                                key={item.label}
                                onClick={() => { item.act(); setKeyMarkerMenu(null); }}
                                onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.08)'; }}
                                onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
                                style={{ padding: '0.35dvw 0.8dvw', cursor: 'pointer' }}
                            >{item.label}</div>
                        ))}
                    </div>
                </>
            )}

            {/* The marker's menu, fixed so the view's clipping does not cut it
                off, and standing on the marker since the player sits near the
                foot of the window */}
            {markerMenu !== null && (() => {
                const diverged = divergedRegions.find(d => d.index === markerMenu.index);
                const chords = diverged ? diverged.chords : [];
                const item = (key: string, text: string, onPick: () => void) => (
                    <div
                        key={key}
                        onClick={() => { onPick(); setMarkerMenu(null); }}
                        onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.08)'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
                        style={{ padding: '0.35dvw 0.8dvw', cursor: 'pointer' }}
                    >{text}</div>
                );
                return (
                    <>
                        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 999 }} onClick={() => setMarkerMenu(null)} />
                        <div style={{
                            position: 'fixed', left: markerMenu.left, bottom: window.innerHeight - markerMenu.top + 4, zIndex: 1000,
                            backgroundColor: '#1a1a1a', border: '0.05dvw solid rgba(255, 255, 255, 0.3)', borderRadius: '0.3dvw',
                            padding: '0.2dvw 0', fontSize: '0.75dvw', color: '#fff', whiteSpace: 'nowrap',
                        }}>
                            {item('chart', chords.length > 1 ? 'Update the chart chords from this region' : 'Update the chart chord from this region',
                                () => onUpdateChartFromRegion(markerMenu.index))}
                            {/* With two or more chart chords dated to the region,
                                one choice each, named, so the region never takes
                                notes from a chord picked for it */}
                            {chords.length === 1 && item(chords[0].id, 'Update this region from the chart chord',
                                () => onUpdateRegionFromChart(markerMenu.index, chords[0].id))}
                            {chords.length > 1 && chords.map(chord => item(chord.id, `Update this region from ${chord.name}`,
                                () => onUpdateRegionFromChart(markerMenu.index, chord.id)))}
                        </div>
                    </>
                );
            })()}
        </div>
    );
};

export default MidiZoomView;
